import type { RumEvent } from "@datadog/browser-rum";

/**
 * Minimal Datadog RUM event shape used to drop Chrome/Safari intervention noise.
 */
export type DatadogRumEventLike = {
  type?: string;
  error?: {
    type?: string;
    message?: string;
    source?: string;
  };
};

const BROWSER_NOISE_ERROR_TYPES = new Set([
  "PreventDefaultPassive",
  "IgnoredEventCancel",
]);

const BROWSER_NOISE_MESSAGE_PATTERNS = [
  /unable to preventdefault inside passive event/i,
  /ignored attempt to cancel a .+ event with cancelable=false/i,
  /can't find variable: _autofillcallbackhandler/i,
  /_autofillcallbackhandler is not defined/i,
];

/**
 * Chrome Reporting API interventions (passive preventDefault, ignored cancel)
 * and Safari autofill’s missing _AutofillCallbackHandler are notices, not
 * application faults. Datadog still records them as errors.
 * @param {DatadogRumEventLike} event - Datadog RUM event from beforeSend
 * @returns {boolean} True when the event should be dropped
 */
export function isBrowserNoiseRumEvent(event: DatadogRumEventLike): boolean {
  if (event.type !== "error") {
    return false;
  }

  const errorType = event.error?.type?.trim() ?? "";
  if (BROWSER_NOISE_ERROR_TYPES.has(errorType)) {
    return true;
  }

  const message = event.error?.message ?? "";
  return BROWSER_NOISE_MESSAGE_PATTERNS.some((pattern) =>
    pattern.test(message),
  );
}

const SHOP_LOGIN_PATH = "/shop-login";

/**
 * True when a URL's path is the public shop-login landing page.
 * @param {string} url - absolute or relative URL to check
 * @returns {boolean} True when the URL points at /shop-login
 */
function isShopLoginUrl(url: string): boolean {
  try {
    return new URL(url, window.location.origin).pathname === SHOP_LOGIN_PATH;
  } catch {
    return false;
  }
}

/**
 * Drops the query string off a shop-login URL and leaves every other URL
 * untouched.
 * @param {string} url - absolute or relative URL to scrub
 * @returns {string} the URL with its query string removed, if it had one
 */
function stripShopLoginQuery(url: string): string {
  if (!isShopLoginUrl(url)) {
    return url;
  }
  const parsed = new URL(url, window.location.origin);
  return `${parsed.origin}${parsed.pathname}`;
}

/**
 * The staff login QR poster encodes an opaque token and an env as a
 * `/shop-login` query string. `sessionReplaySampleRate: 100` and
 * `defaultPrivacyLevel: "allow"` mean RUM otherwise records URLs
 * verbatim, so any event carrying that query string has it stripped
 * before the event leaves the browser. Mutates the event in place, the
 * same way Datadog's own beforeSend redaction recipes work.
 * @param {RumEvent} event - Datadog RUM event from beforeSend
 * @returns {void}
 */
export function scrubShopLoginRumEventUrls(event: RumEvent): void {
  // This runs inside beforeSend, where a throw costs the whole event and can
  // take the SDK's own reporting with it, so nothing here assumes a shape.
  if (!event?.view || typeof event.view.url !== "string") return;

  event.view.url = stripShopLoginQuery(event.view.url);
  if (event.view.referrer) {
    event.view.referrer = stripShopLoginQuery(event.view.referrer);
  }

  if (event.type === "resource" && typeof event.resource?.url === "string") {
    event.resource.url = stripShopLoginQuery(event.resource.url);
  } else if (event.type === "error" && event.error?.resource) {
    event.error.resource.url = stripShopLoginQuery(event.error.resource.url);
  }
}
